import { Fragment } from "react";

import { RottenTomatoesBadge } from "@/components/atoms/RottenTomatoesBadge";
import {
  RottenTomatoesBadgeSize,
  RottenTomatoesBadgeVariant,
} from "@/components/atoms/RottenTomatoesBadge/types";
import { Tile169Size } from "@/components/molecules/Tile169/types";
import {
  isMovie,
  isSeries,
  isSportsEvent,
  MovieResponse,
  SeriesResponse,
} from "@/utils/FetchAssetDetails/types";
import { formatTitleBasicsDate } from "@/utils/TileUtils";
import { useClassNames } from "@/utils/classNames";

import { TileTitleBasicsProps } from "./types";
import styles from "./TileTitleBasics.module.scss";

// Mininum Rotten Tomatoes score to display is 60
const MIN_FRESH_SCORE = 60;

/**
 * TileTitleBasics component displays basic metadata for 16:9 tiles.
 *
 * @param asset - the asset object containing metadata
 * @param tileSize - the size of the tile this metadata is displayed in
 * @param className - Additional CSS class for styling
 * @param attrs - Additional HTML attributes for the div element
 * @returns the TileTitleBasics component
 *
 * @example
 * ```tsx
 * <TileTitleBasics
 *  asset={asset}
 *  tileSize={Tile169Size.XLarge}
 * />;
 * ```
 */
const TileTitleBasics = ({
  asset,
  tileSize,
  className,
  ...attrs
}: TileTitleBasicsProps) => {
  const cn = useClassNames(
    styles,
    styles["tile-title-basics"],
    styles[`tile-size-${tileSize}`],
    className,
  );

  const getItemsList = (): string[] => {
    let items: (string | undefined)[] = [];
    const genre =
      "genres" in asset.attributes && asset.attributes.genres
        ? asset.attributes.genres[0]
        : undefined;

    if (isMovie(asset)) {
      const { year } = (asset as MovieResponse).attributes;
      items = [genre, year.toString()];
    } else if (isSeries(asset)) {
      const { attributes } = asset as SeriesResponse;
      const seasonCount = attributes.availableSeasonCount;
      if (seasonCount === 1) {
        items = [genre, "1 Season"];
      } else {
        items = [genre, `${seasonCount} Seasons`];
      }
    } else if (isSportsEvent(asset)) {
      const date = formatTitleBasicsDate(asset);
      if (date) {
        items = date;
      }
    }

    return items
      .filter((item): item is string => item !== undefined && item !== "")
      .map((item) => item.toString());
  };

  const getCriticScore = (): number | null => {
    if ("fanCriticRating" in asset.attributes) {
      const rating = asset.attributes.fanCriticRating?.[0];
      const score = rating?.criticScore;

      if (score && score >= MIN_FRESH_SCORE) return score;
    }
    return null;
  };

  const items = getItemsList();
  let criticScore = getCriticScore();

  return (
    <div className={cn} {...attrs}>
      {criticScore && (
        <>
          <RottenTomatoesBadge
            variant={RottenTomatoesBadgeVariant.CriticsScore}
            size={
              tileSize === Tile169Size.Medium
                ? RottenTomatoesBadgeSize.Medium
                : RottenTomatoesBadgeSize.Large
            }
            score={criticScore}
          />
          <Dot />
        </>
      )}
      {items.map((item, index) => (
        <Fragment key={index}>
          <span>{item}</span>
          {index !== items.length - 1 && <Dot />}
        </Fragment>
      ))}
    </div>
  );
};

const Dot = () => <span>·</span>;

export { TileTitleBasics, Dot };
